import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { config } from '../constants/config';
import {
  RESOURCE_TYPES,
  RESOURCE_ID_PARAMS,
  HTTP_METHODS
} from '../apiEngine/schemas/parameterSchema';
import {
  createGetThunk,
  createGetCollectionThunk,
  createPutThunk,
  createPostThunk,
  createDeleteThunk,
  createSignedUrlThunk
} from '../apiEngine/thunks/apiThunkCreator';

// Async thunk for fetching all voice actor users
export const fetchActorUsers = createGetCollectionThunk({
  typePrefix: 'voiceActor/fetchActorUsers',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.BASE',
  hintText: "FETCHING ALL VOICE ACTOR USERS",
  transformResponse: (data) => data
});

// Async thunk for fetching a specific voice actor user
export const fetchActorUser = createGetThunk({
  typePrefix: 'voiceActor/fetchActorUser',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.DETAIL',
  hintText: "FETCHING VOICE ACTOR USER",
  transformResponse: (data) => data
});

// Async thunk for updating a voice actor user
export const updateActorUser = createPutThunk({
  typePrefix: 'voiceActor/updateActorUser',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.DETAIL',
  hintText: "UPDATING VOICE ACTOR USER",
  paramMapping: { 'actorId': 'actorId' },  // Map actorId to actorId for API routes
  // Using standard parameter name actorId directly, no need for mapping
  transformResponse: (data) => data
});

// Async thunk for fetching voice actor assignments
export const fetchActorAssignments = createGetThunk({
  typePrefix: 'voiceActor/fetchActorAssignments',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.ASSIGNMENTS',
  hintText: "FETCHING VOICE ACTOR ASSIGNMENTS",
  transformResponse: (data) => data
});

// Async thunk for fetching actor portfolios
export const fetchActorPortfolios = createGetThunk({
  typePrefix: 'voiceActor/fetchActorPortfolios',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.PORTFOLIOS',
  hintText: "FETCHING ACTOR PORTFOLIOS",
  paramName: 'actorUserId',  // Changed from 'actorId' to match component usage
  paramMapping: { 'actorUserId': 'actorId' }, // Map actorUserId to actorId for API routes
  transformResponse: (data) => data
});

/**
 * Async thunk for fetching a specific actor portfolio
 * @param {Object} params - The parameters object
 * @param {string} params.portfolioId - The ID of the portfolio to fetch
 * @returns {Promise<Object>} The portfolio data
 */
export const fetchActorPortfolio = createGetThunk({
  typePrefix: 'voiceActor/fetchActorPortfolio',
  resourceType: RESOURCE_TYPES.PORTFOLIO,
  routeKey: 'PORTFOLIOS.DETAIL',
  hintText: "FETCHING ACTOR PORTFOLIO",
  transformResponse: (data) => data
});

/**
 * Async thunk for fetching character variations
 * @param {string} characterId - The ID of the character to fetch variations for
 * @returns {Promise<Object>} The character variations data
 */

export const fetchCharacterVariations = createGetThunk({
  typePrefix: 'voiceActor/fetchCharacterVariations',
  resourceType: RESOURCE_TYPES.CHARACTER,
  routeKey: 'CHARACTERS.VARIATIONS',
  hintText: "FETCHING CHARACTER VARIATIONS",
  transformResponse: (data, { resourceId }) => {
    return {
      characterId: resourceId,
      variations: data
    };
  }
});
// Async thunk for creating an actor portfolio
export const createActorPortfolio = createPostThunk({
  typePrefix: 'voiceActor/createActorPortfolio',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.PORTFOLIOS',
  hintText: "CREATING ACTOR PORTFOLIO",
  paramName: 'actorUserId',  // Changed from 'actorId' to match component usage
  paramMapping: { 'actorUserId': 'actorId' }, // Map actorUserId to actorId for API routes
  transformResponse: (data) => data
});

/**
 * Async thunk for updating an actor portfolio
 * @param {Object} params - The parameters object
 * @param {string} params.portfolioId - The ID of the portfolio to update
 * @param {Object} params.portfolioData - The portfolio data to update with
 * @returns {Promise<Object>} The updated portfolio data
 */
export const updateActorPortfolio = createPutThunk({
  typePrefix: 'voiceActor/updateActorPortfolio',
  resourceType: RESOURCE_TYPES.PORTFOLIO,
  routeKey: 'PORTFOLIOS.DETAIL',
  hintText: "UPDATING ACTOR PORTFOLIO",
  transformResponse: (data) => data
});

/**
 * Async thunk for deleting an actor portfolio
 * @param {Object} params - The parameters object
 * @param {string} params.portfolioId - The ID of the portfolio to delete
 * @returns {Promise<Object>} The result object with the deleted portfolio ID
 */
export const deleteActorPortfolio = createDeleteThunk({
  typePrefix: 'voiceActor/deleteActorPortfolio',
  resourceType: RESOURCE_TYPES.PORTFOLIO,
  routeKey: 'PORTFOLIOS.DETAIL',
  hintText: "DELETING ACTOR PORTFOLIO",
  transformResponse: (data, { portfolioId }) => {
    return { portfolioId };
  }
});

// Old implementation removed

/**
 * Async thunk for fetching recorded characters for a portfolio
 * @param {Object} params - The parameters object
 * @param {string} params.portfolioId - The ID of the portfolio to fetch characters for
 * @returns {Promise<Array>} The array of characters
 */
export const fetchRecordedCharacters = createGetThunk({
  typePrefix: 'voiceActor/fetchRecordedCharacters',
  resourceType: RESOURCE_TYPES.PORTFOLIO,
  routeKey: 'PORTFOLIOS.CHARACTERS',
  hintText: "FETCHING RECORDED CHARACTERS",
  transformResponse: (data) => Array.isArray(data) ? data : []
});


/**
 * Async thunk for fetching a specific recorded character
 * @param {Object} params - The parameters object
 * @param {string} params.characterId - The ID of the character to fetch
 * @returns {Promise<Object>} The character data
 */
export const fetchRecordedCharacter = createGetThunk({
  typePrefix: 'voiceActor/fetchRecordedCharacter',
  resourceType: RESOURCE_TYPES.CHARACTER,
  routeKey: 'CHARACTERS.DETAIL',
  hintText: "FETCHING RECORDED CHARACTER",
  transformResponse: (data) => data
});

/**
 * Async thunk for creating a recorded character
 * @param {Object} params - The parameters object
 * @param {string} params.portfolioId - The ID of the portfolio to create the character in
 * @param {Object} params.characterData - The character data to create
 * @returns {Promise<Object>} The created character data
 */
export const createRecordedCharacter = createPostThunk({
  typePrefix: 'voiceActor/createRecordedCharacter',
  resourceType: RESOURCE_TYPES.PORTFOLIO,
  routeKey: 'PORTFOLIOS.CHARACTERS',
  hintText: "CREATING RECORDED CHARACTER",
  transformResponse: (data) => data
});

/**
 * Async thunk for updating a recorded character
 * @param {Object} params - The parameters object
 * @param {string} params.characterId - The ID of the character to update
 * @param {Object} params.characterData - The character data to update with
 * @returns {Promise<Object>} The updated character data
 */
export const updateRecordedCharacter = createPutThunk({
  typePrefix: 'voiceActor/updateRecordedCharacter',
  resourceType: RESOURCE_TYPES.CHARACTER,
  routeKey: 'CHARACTERS.DETAIL',
  hintText: "UPDATING RECORDED CHARACTER",
  transformResponse: (data) => data
});

/**
 * Async thunk for deleting a recorded character
 * @param {Object} params - The parameters object
 * @param {string} params.characterId - The ID of the character to delete
 * @returns {Promise<Object>} The result object with the deleted character ID
 */
export const deleteRecordedCharacter = createDeleteThunk({
  typePrefix: 'voiceActor/deleteRecordedCharacter',
  resourceType: RESOURCE_TYPES.CHARACTER,
  routeKey: 'CHARACTERS.DETAIL',
  hintText: "DELETING RECORDED CHARACTER",
  transformResponse: (data, { characterId }) => {
    return { characterId };
  }
});

// Second declaration removed to fix duplicate error

/**
 * Async thunk for fetching all character variations for all characters
 * @param {Object} params - The parameters object
 * @param {Array<string>} params.characterIds - The IDs of the characters to fetch variations for
 * @returns {Promise<Object>} A map of character IDs to their variations
 */
export const fetchAllCharacterVariations = createGetThunk({
  typePrefix: 'voiceActor/fetchAllCharacterVariations',
  resourceType: RESOURCE_TYPES.CHARACTER,
  routeKey: 'CHARACTERS.WITH_VARIATIONS',
  hintText: "FETCHING ALL CHARACTER VARIATIONS",
  paramName: 'characterIds',
  transformParams: (params) => {
    // Convert array to JSON string for query parameter
    if (params.characterIds && Array.isArray(params.characterIds)) {
      return { characterIds: JSON.stringify(params.characterIds) };
    }
    return params;
  },
  transformResponse: (data) => data
});

/**
 * Async thunk for fetching a specific character variation
 * @param {Object} params - The parameters object
 * @param {string} params.variationId - The ID of the variation to fetch
 * @returns {Promise<Object>} The variation data
 */
export const fetchCharacterVariation = createGetThunk({
  typePrefix: 'voiceActor/fetchCharacterVariation',
  resourceType: RESOURCE_TYPES.VARIATION,
  routeKey: 'VARIATIONS.DETAIL',
  hintText: "FETCHING CHARACTER VARIATION",
  transformResponse: (data) => data
});

/**
 * Async thunk for creating a character variation
 * @param {Object} params - The parameters object
 * @param {string} params.characterId - The ID of the character to create a variation for
 * @param {Object} params.variationData - The variation data to create
 * @returns {Promise<Object>} The result object with character ID and created variation
 */
export const createCharacterVariation = createPostThunk({
  typePrefix: 'voiceActor/createCharacterVariation',
  resourceType: RESOURCE_TYPES.CHARACTER,
  routeKey: 'CHARACTERS.VARIATIONS',
  hintText: "CREATING CHARACTER VARIATION",
  transformResponse: (data, { characterId }) => {
    return { characterId, variation: data };
  }
});

/**
 * Async thunk for updating a character variation
 * @param {Object} params - The parameters object
 * @param {string} params.variationId - The ID of the variation to update
 * @param {string} params.characterId - The ID of the character the variation belongs to
 * @param {Object} params.variationData - The variation data to update with
 * @returns {Promise<Object>} The result object with character ID and updated variation
 */
export const updateCharacterVariation = createPutThunk({
  typePrefix: 'voiceActor/updateCharacterVariation',
  resourceType: RESOURCE_TYPES.VARIATION,
  routeKey: 'VARIATIONS.DETAIL',
  hintText: "UPDATING CHARACTER VARIATION",
  transformResponse: (data, { characterId }) => {
    return { characterId, variation: data };
  }
});

/**
 * Async thunk for deleting a character variation
 * @param {Object} params - The parameters object
 * @param {string} params.variationId - The ID of the variation to delete
 * @param {string} params.characterId - The ID of the character the variation belongs to
 * @returns {Promise<Object>} The result object with character ID and variation ID
 */
export const deleteCharacterVariation = createDeleteThunk({
  typePrefix: 'voiceActor/deleteCharacterVariation',
  resourceType: RESOURCE_TYPES.VARIATION,
  routeKey: 'VARIATIONS.DETAIL',
  hintText: "DELETING CHARACTER VARIATION",
  transformResponse: (data, { characterId, variationId }) => {
    return { characterId, variationId };
  }
});

/**
 * Async thunk for initializing actor schema
 * @param {Object} actorData - The actor data to create
 * @returns {Promise<Object>} The created actor data
 */
export const initializeActorSchema = createPostThunk({
  typePrefix: 'voiceActor/initializeActorSchema',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.SCHEMA',
  hintText: "INITIALIZING ACTOR SCHEMA",
  transformResponse: (data) => data
});

/**
 * Async thunk for deleting a voice actor user
 * @param {Object} params - The parameters object
 * @param {string} params.actorId - The ID of the actor to delete
 * @returns {Promise<Object>} The result object with the deleted actor ID
 */
export const deleteActorUser = createDeleteThunk({
  typePrefix: 'voiceActor/deleteActorUser',
  resourceType: RESOURCE_TYPES.ACTOR,
  routeKey: 'VOICE_ACTORS.DETAIL',
  hintText: "DELETING VOICE ACTOR USER",
  transformResponse: (data, { actorId }) => {
    return { actorId };
  }
});

/**
 * Async thunk for batch fetching signed URLs
 * @param {Object} params - The parameters object
 * @param {Array<Object>} params.urls - The URLs to fetch signed URLs for
 * @param {string} params.userId - The ID of the user
 * @returns {Promise<Object>} The result object with the URL map and user ID
 */
// export const batchFetchSignedUrls = createAsyncThunk(
//   'voiceActor/batchFetchSignedUrls',
//   async ({ urls, userId }, { rejectWithValue, dispatch }) => {
//     try {
//       // Validate userId
//       if (!userId) {
//         console.error('batchFetchSignedUrls: Missing userId parameter');
//         return rejectWithValue({ error: 'User ID is required' });
//       }

//       // Validate urls
//       if (!urls || !Array.isArray(urls) || urls.length === 0) {
//         console.error('batchFetchSignedUrls: Missing or empty urls parameter');
//         return rejectWithValue({ error: 'URLs are required' });
//       }

//       console.log(`Batch fetching signed URLs for ${urls.length} URLs`);

//       // Import the API client and routes
//       const { apiClient } = require('../apiTools/apiClient');
//       const { apiRoutes } = require('../apiTools/constants/apiRoutes');

//       // Since there's no batch endpoint, we'll process each URL individually
//       // and build a map of original URLs to signed URLs
//       const urlMap = {};

//       // Process each URL sequentially to avoid overwhelming the server
//       for (const url of urls) {
//         console.log('BATCH SIGNED URL', url.url);
//         try {
//           // Extract parameters from the URL
//           const urlParts = url.url.split('/');
//           const fileName = urlParts.pop(); // Get the filename (last part)

//           // Determine if this is a character avatar or voice actor profile image
//           const isVoiceActorProfileImage = urlParts.includes('aai-actors') && urlParts.includes('profile-img');
//           const isCharacterAvatarImage = urlParts.includes('act-characters') && urlParts.includes('avatar');

//           let requestBody = {};

//           if (isVoiceActorProfileImage) {
//             // Handle voice actor profile image
//             const folderName = urlParts.pop(); // Get the folder name (profile-img)
//             const actorId = urlParts.pop(); // Get the actor ID

//             requestBody = {
//               requestType: 'FOLDER',
//               userId: userId,
//               folderName: `aai-actors/${actorId}/${folderName}`,
//               fileName: fileName
//             };
//           } else if (isCharacterAvatarImage) {
//             // Handle character avatar image
//             const folderName = urlParts.pop(); // Get the folder name (avatar)
//             const characterId = urlParts.pop(); // Get the character ID
//             const portfolioFolder = urlParts.pop(); // Get the portfolio folder (act-portfolios)
//             const portfolioId = urlParts.pop(); // Get the portfolio ID
//             const actorFolder = urlParts.pop(); // Get the actor folder (aai-actors)
//             const actorId = urlParts.pop(); // Get the actor ID

//             requestBody = {
//               requestType: 'FOLDER',
//               userId: userId,
//               folderName: `aai-actors/${actorId}/act-portfolios/${portfolioId}/act-characters/${characterId}/${folderName}`,
//               fileName: fileName
//             };
//           } else {
//             // Standard handling for other types of URLs
//             const folderName = urlParts.pop(); // Get the folder name

//             requestBody = {
//               requestType: 'FOLDER',
//               userId: userId,
//               folderName: folderName,
//               fileName: fileName
//             };
//           }

//           // Use the API client to make the request
//           const response = await apiClient.post(
//             apiRoutes.UTILS.GET_SIGNED_URL_BY_TYPE,
//             requestBody
//           );

//           urlMap[url.url] = response.data.signedUrl;
//         } catch (error) {
//           console.warn(`Error processing URL ${url.url}:`, error);
//         }
//       }

//       return { urlMap, userId };
//     } catch (error) {
//       console.error('Error in batchFetchSignedUrls:', error);
//       return rejectWithValue(
//         error.response?.data || { error: error.message || 'Unknown error' }
//       );
//     }
//   }
// );

/**
 * Async thunk for fetching and caching signed URLs
 * @param {Object} params - The parameters object
 * @param {string} params.url - The URL to fetch a signed URL for
 * @param {string} params.userId - The ID of the user
 * @returns {Promise<Object>} The result object with the original URL, signed URL, and user ID
 */
export const fetchAndCacheSignedUrl = createAsyncThunk(
  'voiceActor/fetchAndCacheSignedUrl',
  async ({ url, userId }, { rejectWithValue }) => {
    try {
      // Validate url and userId
      if (!url) {
        console.error('fetchAndCacheSignedUrl: Missing url parameter');
        return rejectWithValue({ error: 'URL is required' });
      }

      if (!userId) {
        console.error('fetchAndCacheSignedUrl: Missing userId parameter');
        return rejectWithValue({ error: 'User ID is required' });
      }

      console.log(`Fetching signed URL for: ${url}`);

      // Import the API client and routes
      const { apiClient } = require('../apiTools/apiClient');
      const { apiRoutes } = require('../apiTools/constants/apiRoutes');

      // Extract parameters from the URL
      const urlParts = url.split('/');
      const fileName = urlParts.pop(); // Get the filename (last part)

      // Determine if this is a character avatar or voice actor profile image
      const isVoiceActorProfileImage = urlParts.includes('aai-actors') && urlParts.includes('profile-img');
      const isCharacterAvatarImage = urlParts.includes('act-characters') && urlParts.includes('avatar');
      const isVoiceFile = urlParts.includes('act-variations');

      let requestBody = {};

      if (isVoiceActorProfileImage) {
        // Handle voice actor profile image
        const folderName = urlParts.pop(); // Get the folder name (profile-img)
        const actorId = urlParts.pop(); // Get the actor ID

        requestBody = {
          requestType: 'FOLDER',
          userId: userId,
          folderName: `aai-actors/${actorId}/${folderName}`,
          fileName: fileName
        };
      } else if (isCharacterAvatarImage) {
        // Handle character avatar image
        const folderName = urlParts.pop(); // Get the folder name (avatar)
        const characterId = urlParts.pop(); // Get the character ID
        const portfolioFolder = urlParts.pop(); // Get the portfolio folder (act-portfolios)
        const portfolioId = urlParts.pop(); // Get the portfolio ID
        const actorFolder = urlParts.pop(); // Get the actor folder (aai-actors)
        const actorId = urlParts.pop(); // Get the actor ID

        requestBody = {
          requestType: 'FOLDER',
          userId: userId,
          folderName: `aai-actors/${actorId}/act-portfolios/${portfolioId}/act-characters/${characterId}/${folderName}`,
          fileName: fileName
        };
      } else if (isVoiceFile) {
        // Handle voice file
        const variationId = urlParts.pop(); // Get the variation ID
        const variationsFolder = urlParts.pop(); // Get the variations folder (act-variations)
        const characterId = urlParts.pop(); // Get the character ID
        const charactersFolder = urlParts.pop(); // Get the characters folder (act-characters)
        const portfolioId = urlParts.pop(); // Get the portfolio ID
        const portfoliosFolder = urlParts.pop(); // Get the portfolios folder (act-portfolios)
        const actorId = urlParts.pop(); // Get the actor ID
        const actorsFolder = urlParts.pop(); // Get the actors folder (aai-actors)

        requestBody = {
          requestType: 'VOICE_FILE',
          userId: userId,
          actorUuid: actorId,
          portfolioUuid: portfolioId,
          characterUuid: characterId,
          variationUuid: variationId,
          fileName: fileName
        };
      } else {
        // Standard handling for other types of URLs
        const folderName = urlParts.pop(); // Get the folder name

        requestBody = {
          requestType: 'FOLDER',
          userId: userId,
          folderName: folderName,
          fileName: fileName
        };
      }

      // Use the API client to make the request
      const response = await apiClient.post(
        apiRoutes.UTILS.GET_SIGNED_URL_BY_TYPE,
        requestBody
      );

      return { originalUrl: url, signedUrl: response.data.signedUrl, userId };
    } catch (error) {
      console.error('Error in fetchAndCacheSignedUrl:', error);
      return rejectWithValue(
        error.response?.data || { error: error.message || 'Unknown error' }
      );
    }
  }
);

/**
 * Async thunk for fetching and caching signed URLs (minimalist version)
 * This version simply passes the full URL to the server without any parsing or reconstruction.
 * Uses a specialized thunk creator for signed URLs.
 * 
 * @param {Object} params - The parameters object
 * @param {string} params.url - The URL to fetch a signed URL for
 * @param {string} params.userId - The ID of the user
 * @returns {Promise<Object>} The result object with the original URL, signed URL, and user ID
 */
export const getSignedS3Url = createSignedUrlThunk({
  typePrefix: 'voiceActor/getSignedS3Url',
  routeKey: 'UTILS.GET_SIGNED_URL_BY_RAW_PATH',
  hintText: "GETTING SIGNED S3 URL",
  transformResponse: (data, { param }) => {
    return {
      originalUrl: param.url,
      signedUrl: data.signedUrl,
      userId: param.userId
    };
  }
});

// Async thunk for ensuring selections are loaded
export const ensureSelectionsLoaded = createAsyncThunk(
  'voiceActor/ensureSelectionsLoaded',
  async (_, { getState, dispatch }) => {
    console.log('[REDUX] Ensuring selections are loaded');
    const state = getState();
    const { selectionId } = state.user.voiceActor;

    // Create a local flag to track if we've already dispatched actions
    let actionsDispatched = false;

    // If we have an ID but no data, fetch the data
    // Add guard clauses to prevent fetching with undefined or invalid IDs
    if (selectionId.actorUser &&
      selectionId.actorUser !== 'undefined' &&
      selectionId.actorUser !== 'null' &&
      !selectSelectedActorUser(state) &&
      !actionsDispatched) {
      console.log(`[REDUX] Fetching actor user ${selectionId.actorUser}`);
      try {
        const resultAction = await dispatch(fetchActorUser({ actorId: selectionId.actorUser }));

        // Check if the action was rejected (e.g., 404 Not Found)
        if (fetchActorUser.rejected.match(resultAction)) {
          console.log(`[REDUX] Actor user ${selectionId.actorUser} not found, clearing selection`);
          dispatch(clearSelection('actorUser'));
        }
      } catch (error) {
        console.error(`[REDUX] Error fetching actor user ${selectionId.actorUser}:`, error);
        dispatch(clearSelection('actorUser'));
      }
      actionsDispatched = true;
      return;
    }

    if (selectionId.portfolio &&
      selectionId.portfolio !== 'undefined' &&
      selectionId.portfolio !== 'null' &&
      !selectSelectedPortfolio(state) &&
      !actionsDispatched) {
      console.log(`[REDUX] Fetching portfolio ${selectionId.portfolio}`);
      try {
        const resultAction = await dispatch(fetchActorPortfolio({ portfolioId: selectionId.portfolio }));

        // Check if the action was rejected (e.g., 404 Not Found)
        if (fetchActorPortfolio.rejected.match(resultAction)) {
          console.log(`[REDUX] Portfolio ${selectionId.portfolio} not found, clearing selection`);
          dispatch(clearSelection('portfolio'));
        }
      } catch (error) {
        console.error(`[REDUX] Error fetching portfolio ${selectionId.portfolio}:`, error);
        dispatch(clearSelection('portfolio'));
      }
      actionsDispatched = true;
      return;
    }

    if (selectionId.character &&
      selectionId.character !== 'undefined' &&
      selectionId.character !== 'null' &&
      !selectSelectedCharacter(state) &&
      !actionsDispatched) {
      console.log(`[REDUX] Fetching character ${selectionId.character}`);
      try {
        const resultAction = await dispatch(fetchRecordedCharacter({ characterId: selectionId.character }));

        // Check if the action was rejected (e.g., 404 Not Found)
        if (fetchRecordedCharacter.rejected.match(resultAction)) {
          console.log(`[REDUX] Character ${selectionId.character} not found, clearing selection`);
          dispatch(clearSelection('character'));
        }
      } catch (error) {
        console.error(`[REDUX] Unexpected error fetching character ${selectionId.character}:`, error);
        dispatch(clearSelection('character'));
      }
      actionsDispatched = true;
      return;
    }

    if (selectionId.variation &&
      selectionId.variation !== 'undefined' &&
      selectionId.variation !== 'null' &&
      !selectSelectedVariation(state) &&
      !actionsDispatched) {
      console.log(`[REDUX] Fetching variation ${selectionId.variation}`);
      try {
        const resultAction = await dispatch(fetchCharacterVariation({ variationId: selectionId.variation }));

        // Check if the action was rejected (e.g., 404 Not Found)
        if (fetchCharacterVariation.rejected.match(resultAction)) {
          console.log(`[REDUX] Variation ${selectionId.variation} not found, clearing selection`);
          dispatch(clearSelection('variation'));
        }
      } catch (error) {
        console.error(`[REDUX] Error fetching variation ${selectionId.variation}:`, error);
        dispatch(clearSelection('variation'));
      }
    }

    return true;
  }
);

// Async thunk for initializing selections once
export const initializeSelections = createAsyncThunk(
  'voiceActor/initializeSelections',
  async (_, { dispatch, getState }) => {
    console.log('[REDUX] Initializing selections');

    // Check if selections have already been initialized
    const state = getState();
    if (state.user.voiceActor.selectionsInitialized) {
      console.log('[REDUX] Selections already initialized, skipping');
      return true;
    }

    await dispatch(ensureSelectionsLoaded());
    return true;
  }
);

// Initial state
const initialState = {
  // Actor users
  actorUsers: [],
  actorUserStatus: 'idle',
  actorUserError: null,
  isNewActorMode: true, // Flag to indicate "New Voice Actor" mode

  // Assignments
  assignments: [],
  assignmentStatus: 'idle',
  assignmentError: null,

  // Portfolios
  actorPortfolios: [],
  portfolioStatus: 'idle',
  portfolioError: null,

  // Flat arrays for the new nested structure
  flatPortfolios: [],
  flatCharacters: [],
  flatVariations: [],

  // Characters
  recordedCharacters: [],
  characterStatus: 'idle',
  characterError: null,

  // Character variations - Legacy structure (kept for backward compatibility)
  characterVariations: {},
  characterVariationsMap: {},
  variationStatus: 'idle',
  variationError: null,

  // Normalized variations structure
  variations: {
    byId: {}, // Variations indexed by ID
    byCharacter: {}, // Character IDs mapped to arrays of variation IDs
    loadingStates: {}, // Loading states by character ID
    operationStatus: {
      create: 'idle',
      update: 'idle',
      delete: 'idle',
      upload: 'idle'
    },
    operationErrors: {
      create: null,
      update: null,
      delete: null,
      upload: null
    }
  },

  // Enhanced characters (with additional metadata)
  enhancedCharacters: [],

  // Selections - New centralized selection system
  selectionId: {
    actorUser: null,
    portfolio: null,
    character: null,
    variation: null
  },
  persistSelections: true,
  selectionsInitialized: false, // Flag to track if selections have been initialized

  // Cache for variations
  cache: {},

  // Signed URLs
  signedUrls: {},
  signedUrlStatus: 'idle',

  // General status
  status: 'idle',
  error: null
};

// Create the slice
const voiceActorSlice = createSlice({
  name: 'voiceActor',
  initialState,
  reducers: {
    // Actor user reducers
    clearSelectedActorUser: (state) => {
      state.selectedActorUser = null;
      state.isNewActorMode = true; // Set to "New Voice Actor" mode when clearing selection
    },
    clearActorUserErrors: (state) => {
      state.actorUserError = null;
    },
    // Explicitly set the actor mode
    setActorMode: (state, action) => {
      state.isNewActorMode = action.payload;
    },

    // Portfolio reducers
    clearPortfolioErrors: (state) => {
      state.portfolioError = null;
    },
    portfolioError: (state, action) => {
      state.portfolioError = action.payload;
    },

    // Character reducers
    clearCharacterErrors: (state) => {
      state.characterError = null;
    },

    // Variation reducers
    clearVariationErrors: (state) => {
      state.variationError = null;
    },
    clearSelectedVariation: (state) => {
      state.selectedVariation = null;
    },
    resetVariationState: (state) => {
      state.characterVariations = {};
      state.selectedVariation = null;
      state.variationStatus = 'idle';
      state.variationError = null;
    },

    // Selection reducers
    setSelection: (state, action) => {
      const { level, id } = action.payload;
      if (!state.selectionId) {
        state.selectionId = {};
      }
      state.selectionId[level] = id;
      console.log(`Selection set: ${level} = ${id}`);
    },
    clearSelection: (state, action) => {
      const level = action.payload;
      if (level) {
        // Clear specific level
        if (state.selectionId) {
          state.selectionId[level] = null;
          console.log(`Selection cleared: ${level}`);
        }
      } else {
        // Clear all selections
        state.selectionId = {
          actorUser: null,
          portfolio: null,
          character: null,
          variation: null
        };
        console.log('All selections cleared');
      }
    },
    setPersistSelections: (state, action) => {
      state.persistSelections = action.payload;
    },

    // Enhanced characters reducer
    updateEnhancedCharacters: (state) => {
      // Start with an empty array
      state.enhancedCharacters = [];

      // Only proceed if we have both characters and variations
      if (state.recordedCharacters && state.recordedCharacters.length > 0 &&
        state.characterVariationsMap && Object.keys(state.characterVariationsMap).length > 0) {

        // Map over each character to create enhanced versions
        state.enhancedCharacters = state.recordedCharacters.map(character => {
          // Get variations for this character from the map
          const variations = state.characterVariationsMap[character.uuid] || [];

          // Return an enhanced character with variations included
          return {
            ...character,
            variations,
            // Add additional metadata that might be useful
            variationCount: variations.length,
            recordedVariationCount: variations.filter(v => v.s3Url).length,
            hasRecordings: variations.some(v => v.s3Url),
            // Store lastUpdated as ISO string for serializability
            lastUpdated: variations.length > 0
              ? new Date(Math.max(...variations.map(v => new Date(v.updatedAt || 0).getTime()))).toISOString()
              : typeof character.updatedAt === 'string'
                ? character.updatedAt
                : new Date(character.updatedAt || 0).toISOString()
          };
        });
      }
    },

    // Cache reducers
    updateCache: (state, action) => {
      const { key, data } = action.payload;

      // Store in cache
      state.cache[key] = data;

      // If this is the voiceLabCompositeData, also update the main state
      if (key === 'voiceLabCompositeData' && data) {
        console.log('Updating Redux store with composite data:', {
          actorUsers: data.actorUsers?.length || 0,
          portfolios: data.portfolios?.length || 0,
          characters: data.characters?.length || 0,
          variationsMap: Object.keys(data.variationsMap || {}).length,
          flatPortfolios: data.flatPortfolios?.length || 0,
          flatCharacters: data.flatCharacters?.length || 0,
          flatVariations: data.flatVariations?.length || 0
        });

        // Update actor users if available
        if (data.actorUsers && Array.isArray(data.actorUsers)) {
          state.actorUsers = data.actorUsers;
          state.status = 'succeeded';
        }

        // Update portfolios if available
        if (data.portfolios && Array.isArray(data.portfolios)) {
          state.actorPortfolios = data.portfolios;
          state.portfolioStatus = 'succeeded';
        }

        // Update characters if available
        if (data.characters && Array.isArray(data.characters)) {
          state.recordedCharacters = data.characters;
          state.characterStatus = 'succeeded';
        }

        // Update variations map if available
        if (data.variationsMap && typeof data.variationsMap === 'object') {
          state.characterVariationsMap = data.variationsMap;
          state.variationStatus = 'succeeded';

          // Also update the enhanced characters
          if (state.recordedCharacters.length > 0) {
            voiceActorSlice.caseReducers.updateEnhancedCharacters(state);
          }
        }

        // Update flat arrays if available
        if (data.flatPortfolios && Array.isArray(data.flatPortfolios)) {
          state.flatPortfolios = data.flatPortfolios;
        }

        if (data.flatCharacters && Array.isArray(data.flatCharacters)) {
          state.flatCharacters = data.flatCharacters;
        }

        if (data.flatVariations && Array.isArray(data.flatVariations)) {
          state.flatVariations = data.flatVariations;
        }
      }
    },
    clearCache: (state) => {
      state.cache = {};
    },

    // Normalized variations reducers
    startVariationOperation: (state, action) => {
      const { operation } = action.payload;
      state.variations.operationStatus[operation] = 'loading';
      state.variations.operationErrors[operation] = null;
    },

    endVariationOperation: (state, action) => {
      const { operation, error } = action.payload;
      state.variations.operationStatus[operation] = error ? 'failed' : 'succeeded';
      state.variations.operationErrors[operation] = error;
    },

    // Optimistic create
    addVariationOptimistic: (state, action) => {
      const { variation, characterId } = action.payload;
      // Add to byId dictionary
      state.variations.byId[variation.uuid] = variation;
      // Add to character's list
      if (!state.variations.byCharacter[characterId]) {
        state.variations.byCharacter[characterId] = [];
      }
      state.variations.byCharacter[characterId].push(variation.uuid);
    },

    // Optimistic update
    updateVariationOptimistic: (state, action) => {
      const { variationId, updates } = action.payload;
      if (state.variations.byId[variationId]) {
        state.variations.byId[variationId] = {
          ...state.variations.byId[variationId],
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Optimistic delete
    deleteVariationOptimistic: (state, action) => {
      const { variationId, characterId } = action.payload;
      // Remove from byId dictionary
      delete state.variations.byId[variationId];
      // Remove from character's list
      if (state.variations.byCharacter[characterId]) {
        state.variations.byCharacter[characterId] =
          state.variations.byCharacter[characterId].filter(id => id !== variationId);
      }
    },

    // Revert optimistic operations
    revertVariationOperation: (state, action) => {
      const { operation, originalData } = action.payload;

      if (operation === 'create') {
        const { variationId, characterId } = originalData;
        // Remove the optimistically added variation
        delete state.variations.byId[variationId];
        if (state.variations.byCharacter[characterId]) {
          state.variations.byCharacter[characterId] =
            state.variations.byCharacter[characterId].filter(id => id !== variationId);
        }
      }
      else if (operation === 'update') {
        const { variationId, originalVariation } = originalData;
        // Restore the original variation
        if (originalVariation) {
          state.variations.byId[variationId] = originalVariation;
        }
      }
      else if (operation === 'delete') {
        const { variation, characterId } = originalData;
        // Restore the deleted variation
        if (variation) {
          state.variations.byId[variation.uuid] = variation;
          if (!state.variations.byCharacter[characterId]) {
            state.variations.byCharacter[characterId] = [];
          }
          if (!state.variations.byCharacter[characterId].includes(variation.uuid)) {
            state.variations.byCharacter[characterId].push(variation.uuid);
          }
        }
      }

      // Reset operation status
      state.variations.operationStatus[operation] = 'idle';
      state.variations.operationErrors[operation] = null;
    },

    // Set loading state for a character's variations
    setVariationsLoadingState: (state, action) => {
      const { characterId, status } = action.payload;
      state.variations.loadingStates[characterId] = status;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchActorUsers
      .addCase(fetchActorUsers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchActorUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.actorUsers = action.payload;
        state.error = null;
      })
      .addCase(fetchActorUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch voice actors';
      })

      // Handle fetchActorUser
      .addCase(fetchActorUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchActorUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selectedActorUser = action.payload;
        state.isNewActorMode = false; // Set to "Edit Actor" mode when an actor is selected
        state.error = null;
      })
      .addCase(fetchActorUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch voice actor';
      })

      // Handle updateActorUser
      .addCase(updateActorUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateActorUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selectedActorUser = action.payload;
        state.isNewActorMode = false; // Ensure we stay in "Edit Actor" mode after updating

        // Also update the actor in the actorUsers array
        const index = state.actorUsers.findIndex(actor => actor.uuid === action.payload.uuid);
        if (index !== -1) {
          state.actorUsers[index] = action.payload;
        }

        state.error = null;
      })
      .addCase(updateActorUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update voice actor';
      })

      // Handle fetchActorAssignments
      .addCase(fetchActorAssignments.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchActorAssignments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.assignments = action.payload;
        state.error = null;
      })
      .addCase(fetchActorAssignments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch voice actor assignments';
      })

      // Handle fetchActorPortfolios
      .addCase(fetchActorPortfolios.pending, (state) => {
        state.portfolioStatus = 'loading';
      })
      .addCase(fetchActorPortfolios.fulfilled, (state, action) => {
        state.portfolioStatus = 'succeeded';
        state.actorPortfolios = Array.isArray(action.payload) ? action.payload : [];
        state.portfolioError = null;
      })
      .addCase(fetchActorPortfolios.rejected, (state, action) => {
        state.portfolioStatus = 'failed';
        state.portfolioError = action.payload || 'Failed to fetch actor portfolios';
      })


      // Handle fetchActorPortfolio
      .addCase(fetchActorPortfolio.pending, (state) => {
        state.portfolioStatus = 'loading';
      })
      .addCase(fetchActorPortfolio.fulfilled, (state, action) => {
        state.portfolioStatus = 'succeeded';
        state.selectedPortfolio = action.payload;
        state.portfolioError = null;
      })
      .addCase(fetchActorPortfolio.rejected, (state, action) => {
        state.portfolioStatus = 'failed';
        state.portfolioError = action.payload || 'Failed to fetch actor portfolio';
      })

      // Handle createActorPortfolio
      .addCase(createActorPortfolio.pending, (state) => {
        state.portfolioStatus = 'loading';
      })
      .addCase(createActorPortfolio.fulfilled, (state, action) => {
        state.portfolioStatus = 'succeeded';
        state.actorPortfolios.push(action.payload);
        state.selectedPortfolio = action.payload;
        state.portfolioError = null;
      })
      .addCase(createActorPortfolio.rejected, (state, action) => {
        state.portfolioStatus = 'failed';
        state.portfolioError = action.payload || 'Failed to create actor portfolio';
      })

      // Handle updateActorPortfolio
      .addCase(updateActorPortfolio.pending, (state) => {
        state.portfolioStatus = 'loading';
      })
      .addCase(updateActorPortfolio.fulfilled, (state, action) => {
        state.portfolioStatus = 'succeeded';
        state.selectedPortfolio = action.payload;

        // Also update the portfolio in the actorPortfolios array
        const index = state.actorPortfolios.findIndex(portfolio => portfolio.id === action.payload.id);
        if (index !== -1) {
          state.actorPortfolios[index] = action.payload;
        }

        state.portfolioError = null;
      })
      .addCase(updateActorPortfolio.rejected, (state, action) => {
        state.portfolioStatus = 'failed';
        state.portfolioError = action.payload || 'Failed to update actor portfolio';
      })

      // Handle deleteActorPortfolio
      .addCase(deleteActorPortfolio.pending, (state) => {
        state.portfolioStatus = 'loading';
      })
      .addCase(deleteActorPortfolio.fulfilled, (state, action) => {
        state.portfolioStatus = 'succeeded';
        state.actorPortfolios = state.actorPortfolios.filter(
          portfolio => portfolio.id !== action.payload.portfolioId
        );
        if (state.selectedPortfolio && state.selectedPortfolio.id === action.payload.portfolioId) {
          state.selectedPortfolio = null;
        }
        state.portfolioError = null;
      })
      .addCase(deleteActorPortfolio.rejected, (state, action) => {
        state.portfolioStatus = 'failed';
        state.portfolioError = action.payload || 'Failed to delete actor portfolio';
      })

      // Handle fetchRecordedCharacters
      .addCase(fetchRecordedCharacters.pending, (state) => {
        state.characterStatus = 'loading';
      })
      .addCase(fetchRecordedCharacters.fulfilled, (state, action) => {
        state.characterStatus = 'succeeded';
        state.recordedCharacters = action.payload;
        state.characterError = null;

        // Update enhanced characters if we have variations data
        if (Object.keys(state.characterVariationsMap).length > 0) {
          // Call our reducer
          voiceActorSlice.caseReducers.updateEnhancedCharacters(state);
        }
      })
      .addCase(fetchRecordedCharacters.rejected, (state, action) => {
        state.characterStatus = 'failed';
        state.characterError = action.payload || 'Failed to fetch recorded characters';
      })

      // Handle fetchRecordedCharacter
      .addCase(fetchRecordedCharacter.pending, (state) => {
        state.characterStatus = 'loading';
      })
      .addCase(fetchRecordedCharacter.fulfilled, (state, action) => {
        state.characterStatus = 'succeeded';
        state.selectedCharacter = action.payload;
        state.characterError = null;
      })
      .addCase(fetchRecordedCharacter.rejected, (state, action) => {
        state.characterStatus = 'failed';
        state.characterError = action.payload || 'Failed to fetch recorded character';
      })

      // Handle createRecordedCharacter
      .addCase(createRecordedCharacter.pending, (state) => {
        state.characterStatus = 'loading';
      })
      .addCase(createRecordedCharacter.fulfilled, (state, action) => {
        state.characterStatus = 'succeeded';
        state.recordedCharacters.push(action.payload);
        state.selectedCharacter = action.payload;
        state.characterError = null;
      })
      .addCase(createRecordedCharacter.rejected, (state, action) => {
        state.characterStatus = 'failed';
        state.characterError = action.payload || 'Failed to create recorded character';
      })

      // Handle updateRecordedCharacter
      .addCase(updateRecordedCharacter.pending, (state) => {
        state.characterStatus = 'loading';
      })
      .addCase(updateRecordedCharacter.fulfilled, (state, action) => {
        state.characterStatus = 'succeeded';
        state.selectedCharacter = action.payload;

        // Also update the character in the recordedCharacters array
        // Use uuid instead of id for finding the character
        const index = state.recordedCharacters.findIndex(character => character.uuid === action.payload.uuid);
        if (index !== -1) {
          state.recordedCharacters[index] = action.payload;
        }

        state.characterError = null;

        // Update enhanced characters
        voiceActorSlice.caseReducers.updateEnhancedCharacters(state);
      })
      .addCase(updateRecordedCharacter.rejected, (state, action) => {
        state.characterStatus = 'failed';
        state.characterError = action.payload || 'Failed to update recorded character';
      })

      // Handle deleteRecordedCharacter
      .addCase(deleteRecordedCharacter.pending, (state) => {
        state.characterStatus = 'loading';
      })
      .addCase(deleteRecordedCharacter.fulfilled, (state, action) => {
        state.characterStatus = 'succeeded';
        state.recordedCharacters = state.recordedCharacters.filter(
          character => character.uuid !== action.payload.characterId
        );
        if (state.selectedCharacter && state.selectedCharacter.uuid === action.payload.characterId) {
          state.selectedCharacter = null;
        }
        state.characterError = null;
      })
      .addCase(deleteRecordedCharacter.rejected, (state, action) => {
        state.characterStatus = 'failed';
        state.characterError = action.payload || 'Failed to delete recorded character';
      })


      // Handle fetchCharacterVariations
      .addCase(fetchCharacterVariations.pending, (state, action) => {
        state.variationStatus = 'loading';
        // Also update the normalized state
        state.variations.loadingStates[action.meta.arg] = 'loading';
      })
      .addCase(fetchCharacterVariations.fulfilled, (state, action) => {
        state.variationStatus = 'succeeded';
        // Update legacy state
        state.characterVariations[action.payload.characterId] = action.payload.variations;
        state.variationError = null;

        // Update normalized state
        const { characterId, variations } = action.payload;

        // Update the loading state
        state.variations.loadingStates[characterId] = 'succeeded';

        // Clear existing variations for this character
        state.variations.byCharacter[characterId] = [];

        // Add all variations to the byId dictionary and update the byCharacter array
        variations.forEach(variation => {
          // Use uuid as the key for consistency
          const variationId = variation.uuid;

          // Add to byId dictionary
          state.variations.byId[variationId] = variation;

          // Add to character's list if not already there
          if (!state.variations.byCharacter[characterId]) {
            state.variations.byCharacter[characterId] = [];
          }

          if (!state.variations.byCharacter[characterId].includes(variationId)) {
            state.variations.byCharacter[characterId].push(variationId);
          }
        });
      })
      .addCase(fetchCharacterVariations.rejected, (state, action) => {
        state.variationStatus = 'failed';
        state.variationError = action.payload || 'Failed to fetch character variations';

        // Update normalized state
        const characterId = action.meta.arg;
        state.variations.loadingStates[characterId] = 'failed';
      })

      // Handle fetchAllCharacterVariations
      .addCase(fetchAllCharacterVariations.pending, (state) => {
        state.variationStatus = 'loading';
      })
      .addCase(fetchAllCharacterVariations.fulfilled, (state, action) => {
        state.variationStatus = 'succeeded';
        state.characterVariationsMap = action.payload;
        state.variationError = null;

        // Update enhanced characters if we have character data
        if (state.recordedCharacters.length > 0) {
          // Call our reducer
          voiceActorSlice.caseReducers.updateEnhancedCharacters(state);
        }
      })
      .addCase(fetchAllCharacterVariations.rejected, (state, action) => {
        state.variationStatus = 'failed';
        state.variationError = action.payload || 'Failed to fetch all character variations';
      })

      // Handle fetchCharacterVariation
      .addCase(fetchCharacterVariation.pending, (state) => {
        state.variationStatus = 'loading';
      })
      .addCase(fetchCharacterVariation.fulfilled, (state, action) => {
        state.variationStatus = 'succeeded';
        state.selectedVariation = action.payload;
        state.variationError = null;
      })
      .addCase(fetchCharacterVariation.rejected, (state, action) => {
        state.variationStatus = 'failed';
        state.variationError = action.payload || 'Failed to fetch character variation';
      })

      // Handle createCharacterVariation
      .addCase(createCharacterVariation.pending, (state, action) => {
        state.variationStatus = 'loading';
        // Update normalized state
        state.variations.operationStatus.create = 'loading';
      })
      .addCase(createCharacterVariation.fulfilled, (state, action) => {
        state.variationStatus = 'succeeded';

        // Update legacy state
        if (!state.characterVariations[action.payload.characterId]) {
          state.characterVariations[action.payload.characterId] = [];
        }
        state.characterVariations[action.payload.characterId].push(action.payload.variation);

        state.selectedVariation = action.payload.variation;
        state.variationError = null;

        // Update normalized state
        const { characterId, variation } = action.payload;

        // Add to byId dictionary
        state.variations.byId[variation.uuid] = variation;

        // Add to character's list
        if (!state.variations.byCharacter[characterId]) {
          state.variations.byCharacter[characterId] = [];
        }

        if (!state.variations.byCharacter[characterId].includes(variation.uuid)) {
          state.variations.byCharacter[characterId].push(variation.uuid);
        }

        // Update operation status
        state.variations.operationStatus.create = 'succeeded';
        state.variations.operationErrors.create = null;

        // Update enhanced characters
        voiceActorSlice.caseReducers.updateEnhancedCharacters(state);
      })
      .addCase(createCharacterVariation.rejected, (state, action) => {
        state.variationStatus = 'failed';
        state.variationError = action.payload || 'Failed to create character variation';

        // Update normalized state
        state.variations.operationStatus.create = 'failed';
        state.variations.operationErrors.create = action.payload || 'Failed to create character variation';
      })

      // Handle updateCharacterVariation
      .addCase(updateCharacterVariation.pending, (state, action) => {
        state.variationStatus = 'loading';
        // Update normalized state
        state.variations.operationStatus.update = 'loading';
      })
      .addCase(updateCharacterVariation.fulfilled, (state, action) => {
        state.variationStatus = 'succeeded';
        state.selectedVariation = action.payload.variation;

        // Update legacy state
        if (state.characterVariations[action.payload.characterId]) {
          const index = state.characterVariations[action.payload.characterId].findIndex(
            variation => variation.id === action.payload.variation.id
          );
          if (index !== -1) {
            state.characterVariations[action.payload.characterId][index] = action.payload.variation;
          }
        }

        state.variationError = null;

        // Update normalized state
        const { characterId, variation } = action.payload;

        // Update in byId dictionary
        state.variations.byId[variation.uuid] = variation;

        // Update operation status
        state.variations.operationStatus.update = 'succeeded';
        state.variations.operationErrors.update = null;

        // Update enhanced characters
        voiceActorSlice.caseReducers.updateEnhancedCharacters(state);
      })
      .addCase(updateCharacterVariation.rejected, (state, action) => {
        state.variationStatus = 'failed';
        state.variationError = action.payload || 'Failed to update character variation';

        // Update normalized state
        state.variations.operationStatus.update = 'failed';
        state.variations.operationErrors.update = action.payload || 'Failed to update character variation';
      })

      // Handle deleteCharacterVariation
      .addCase(deleteCharacterVariation.pending, (state, action) => {
        state.variationStatus = 'loading';
        // Update normalized state
        state.variations.operationStatus.delete = 'loading';
      })
      .addCase(deleteCharacterVariation.fulfilled, (state, action) => {
        state.variationStatus = 'succeeded';

        // Update legacy state
        if (state.characterVariations[action.payload.characterId]) {
          state.characterVariations[action.payload.characterId] = state.characterVariations[action.payload.characterId].filter(
            variation => variation.id !== action.payload.variationId
          );
        }

        if (state.selectedVariation && state.selectedVariation.id === action.payload.variationId) {
          state.selectedVariation = null;
        }

        state.variationError = null;

        // Update normalized state
        const { characterId, variationId } = action.payload;

        // Remove from byId dictionary
        delete state.variations.byId[variationId];

        // Remove from character's list
        if (state.variations.byCharacter[characterId]) {
          state.variations.byCharacter[characterId] =
            state.variations.byCharacter[characterId].filter(id => id !== variationId);
        }

        // Update operation status
        state.variations.operationStatus.delete = 'succeeded';
        state.variations.operationErrors.delete = null;

        // Update enhanced characters
        voiceActorSlice.caseReducers.updateEnhancedCharacters(state);
      })
      .addCase(deleteCharacterVariation.rejected, (state, action) => {
        state.variationStatus = 'failed';
        state.variationError = action.payload || 'Failed to delete character variation';

        // Update normalized state
        state.variations.operationStatus.delete = 'failed';
        state.variations.operationErrors.delete = action.payload || 'Failed to delete character variation';
      })

      // Handle initializeActorSchema
      .addCase(initializeActorSchema.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(initializeActorSchema.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Reset to "New Voice Actor" mode after creating a new actor
        state.isNewActorMode = true;
        state.error = null;
      })
      .addCase(initializeActorSchema.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to initialize actor schema';
      })

      // Handle deleteActorUser
      .addCase(deleteActorUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteActorUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.actorUsers = state.actorUsers.filter(
          actor => actor.id !== action.payload.actorId
        );
        if (state.selectedActorUser && state.selectedActorUser.id === action.payload.actorId) {
          state.selectedActorUser = null;
        }
        // Reset to "New Voice Actor" mode after deleting an actor
        state.isNewActorMode = true;
        state.error = null;
      })
      .addCase(deleteActorUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete actor user';
      })

      // Handle batchFetchSignedUrls
      // .addCase(batchFetchSignedUrls.pending, (state) => {
      //   state.signedUrlStatus = 'loading';
      // })
      // .addCase(batchFetchSignedUrls.fulfilled, (state, action) => {
      //   state.signedUrlStatus = 'succeeded';

      //   // Store the signed URLs in the cache
      //   const { urlMap, userId } = action.payload;
      //   Object.entries(urlMap).forEach(([originalUrl, signedUrl]) => {
      //     const key = `${originalUrl}_${userId}`;
      //     state.signedUrls[key] = signedUrl;
      //   });

      //   state.error = null;
      // })
      // .addCase(batchFetchSignedUrls.rejected, (state, action) => {
      //   state.signedUrlStatus = 'failed';
      //   state.error = action.payload || 'Failed to batch fetch signed URLs';
      // })

      // Handle fetchAndCacheSignedUrl
      .addCase(fetchAndCacheSignedUrl.pending, (state) => {
        state.signedUrlStatus = 'loading';
      })
      .addCase(fetchAndCacheSignedUrl.fulfilled, (state, action) => {
        state.signedUrlStatus = 'succeeded';
        // Store the signed URL in the cache
        const key = `${action.payload.originalUrl}_${action.payload.userId}`;
        state.signedUrls[key] = action.payload.signedUrl;
        state.error = null;
      })
      .addCase(fetchAndCacheSignedUrl.rejected, (state, action) => {
        state.signedUrlStatus = 'failed';
        state.error = action.payload || 'Failed to fetch signed URL';
      })

      // Handle getSignedS3Url
      .addCase(getSignedS3Url.pending, (state) => {
        state.signedUrlStatus = 'loading';
      })
      .addCase(getSignedS3Url.fulfilled, (state, action) => {
        state.signedUrlStatus = 'succeeded';
        // Store the signed URL in the cache
        const key = `${action.payload.originalUrl}_${action.payload.userId}`;
        state.signedUrls[key] = action.payload.signedUrl;
        state.error = null;
      })
      .addCase(getSignedS3Url.rejected, (state, action) => {
        state.signedUrlStatus = 'failed';
        state.error = action.payload || 'Failed to get signed S3 URL';
      })

      // Handle initializeSelections
      .addCase(initializeSelections.pending, (state) => {
        // No state changes needed for pending
      })
      .addCase(initializeSelections.fulfilled, (state) => {
        state.selectionsInitialized = true;
      })
      .addCase(initializeSelections.rejected, (state) => {
        // Even if initialization fails, mark as initialized to prevent infinite retries
        state.selectionsInitialized = true;
      })

      // Handle ensureSelectionsLoaded
      .addCase(ensureSelectionsLoaded.pending, (state) => {
        // No state changes needed for pending
      })
      .addCase(ensureSelectionsLoaded.fulfilled, (state) => {
        // No state changes needed for fulfilled
      })
      .addCase(ensureSelectionsLoaded.rejected, (state) => {
        // No state changes needed for rejected
      });
  }
});

// Export actions
export const {
  // Actor user actions
  clearSelectedActorUser,
  clearActorUserErrors,
  setActorMode,

  // Portfolio actions
  clearPortfolioErrors,
  portfolioError,

  // Character actions
  clearCharacterErrors,

  // Variation actions
  clearVariationErrors,
  clearSelectedVariation,
  resetVariationState,

  // Selection actions
  setSelection,
  clearSelection,
  setPersistSelections,

  // Enhanced characters actions
  updateEnhancedCharacters,

  // Cache actions
  updateCache,
  clearCache,

  // Normalized variations actions
  startVariationOperation,
  endVariationOperation,
  addVariationOptimistic,
  updateVariationOptimistic,
  deleteVariationOptimistic,
  revertVariationOperation,
  setVariationsLoadingState
} = voiceActorSlice.actions;

// Export selectors
// Actor user selectors
export const selectActorUsers = (state) => state.user.voiceActor.actorUsers;
export const selectActorUsersStatus = (state) => state.user.voiceActor.status;
export const selectActorUsersError = (state) => state.user.voiceActor.error;
export const selectIsNewActorMode = (state) => state.user.voiceActor.isNewActorMode;

// Flat array selectors for the new nested structure.
// Stable empty-array fallback — a fresh `[]` per call makes useSelector warn
// ("returned a different result … same parameters") and rerender needlessly.
const EMPTY_ARRAY = Object.freeze([]);
export const selectFlatPortfolios = (state) => state.user.voiceActor.flatPortfolios || EMPTY_ARRAY;
export const selectFlatCharacters = (state) => state.user.voiceActor.flatCharacters || EMPTY_ARRAY;
export const selectFlatVariations = (state) => state.user.voiceActor.flatVariations || EMPTY_ARRAY;

// Nested structure selectors
export const selectPortfoliosByActor = (state, actorId) => {
  if (actorId === 'all') {
    return selectFlatPortfolios(state);
  }

  const actor = selectActorUsers(state).find(a => a.uuid === actorId);
  return actor && actor.portfolios ? actor.portfolios : [];
};

export const selectCharactersByPortfolioId = (state, portfolioId) => {
  if (portfolioId === 'all') {
    return selectFlatCharacters(state);
  }

  const portfolio = selectFlatPortfolios(state).find(p => p.uuid === portfolioId);
  return portfolio && portfolio.characters ? portfolio.characters : [];
};

export const selectVariationsByCharacterId = (state, characterId) => {
  if (characterId === 'all') {
    return selectFlatVariations(state);
  }

  const character = selectFlatCharacters(state).find(c => c.uuid === characterId);
  return character && character.variations ? character.variations : [];
};

// Memoized selector for portfolios with filtered characters
export const selectPortfoliosWithFilteredCharacters = createSelector(
  [
    selectFlatPortfolios,
    selectFlatCharacters,
    (_, filters) => filters
  ],
  (portfolios, characters, filters) => {
    const { actorId, characterId, status, usage } = filters || {};

    // Start with all portfolios or filter by actor
    let filteredPortfolios = actorId === 'all'
      ? portfolios
      : portfolios.filter(p => p.actorId === actorId);

    // Filter portfolios by status and usage if needed
    if (status && status !== 'all') {
      filteredPortfolios = filteredPortfolios.filter(p => p.status === status);
    }

    if (usage && usage !== 'all') {
      filteredPortfolios = filteredPortfolios.filter(p => p.usage === usage);
    }

    // If filtering by character, we need to modify the portfolios to only include that character
    if (characterId && characterId !== 'all') {
      // Find portfolios that contain the character
      const portfoliosWithCharacter = filteredPortfolios.filter(p =>
        p.characters && p.characters.some(c => c.uuid === characterId)
      );

      // For each portfolio, only include the selected character
      return portfoliosWithCharacter.map(p => ({
        ...p,
        characters: p.characters.filter(c => c.uuid === characterId)
      }));
    }

    return filteredPortfolios;
  }
);

// Memoized selector for portfolios with characters
export const selectPortfoliosWithCharacters = createSelector(
  [
    state => state.user.voiceActor.actorPortfolios || [],
    state => state.user.voiceActor.recordedCharacters || [],
    state => state.user.voiceActor.characterVariationsMap || {}
  ],
  (portfolios, characters, variationsMap) => {
    // Log the inputs to help debug
    console.log('selectPortfoliosWithCharacters inputs:', {
      portfoliosCount: portfolios.length,
      charactersCount: characters.length,
      variationsMapKeys: Object.keys(variationsMap).length
    });

    // Group characters by portfolio
    return portfolios.map(portfolio => {
      // Find characters for this portfolio
      const portfolioCharacters = characters.filter(
        character => character.portfolioId === portfolio.uuid
      );

      // Add variations to each character
      const charactersWithVariations = portfolioCharacters.map(character => {
        // Get variations for this character from the variationsMap
        const characterVariations = variationsMap[character.uuid] || [];

        // Return character with variations attached
        return {
          ...character,
          variations: characterVariations
        };
      });

      // Calculate variation count for the portfolio
      const variationCount = charactersWithVariations.reduce(
        (count, character) => count + (character.variations ? character.variations.length : 0),
        0
      );

      return {
        ...portfolio,
        characters: charactersWithVariations,
        variationCount: variationCount
      };
    });
  }
);

// Assignment selectors
export const selectActorAssignments = (state) => state.user.voiceActor.assignments;

// Portfolio selectors
export const selectActorPortfolios = (state) => state.user.voiceActor.actorPortfolios;
export const selectActorPortfoliosStatus = (state) => state.user.voiceActor.portfolioStatus;
export const selectActorPortfoliosError = (state) => state.user.voiceActor.portfolioError;

// Character selectors
export const selectRecordedCharacters = (state) => state.user.voiceActor.recordedCharacters;
export const selectRecordedCharactersStatus = (state) => state.user.voiceActor.characterStatus;
export const selectRecordedCharactersError = (state) => state.user.voiceActor.characterError;
export const selectEnhancedCharacters = (state) => state.user.voiceActor.enhancedCharacters;
export const selectCharactersByPortfolio = (portfolioId) => (state) => {
  return state.user.voiceActor.recordedCharacters.filter(
    character => character.portfolioId === portfolioId
  );
};

// Variation selectors (legacy)
export const selectCharacterVariations = (state) => state.user.voiceActor.characterVariations;
export const selectCharacterVariationsStatus = (state) => state.user.voiceActor.variationStatus;
export const selectCharacterVariationsError = (state) => state.user.voiceActor.variationError;
export const selectCharacterVariationsMap = (state) => state.user.voiceActor.characterVariationsMap;
export const selectCharacterVariationsMapStatus = (state) => state.user.voiceActor.variationStatus;
export const selectCharacterVariationsMapError = (state) => state.user.voiceActor.variationError;

// Normalized variation selectors with memoization
export const selectVariationById = (variationId) => createSelector(
  [state => state.user.voiceActor.variations.byId],
  (byId) => byId[variationId]
);

// Memoized selector for variations by character
export const selectVariationsByCharacter = (characterId) => createSelector(
  [
    state => state.user.voiceActor.variations.byId,
    state => state.user.voiceActor.variations.byCharacter[characterId] || []
  ],
  (byId, variationIds) => {
    return variationIds.map(id => byId[id]).filter(Boolean);
  }
);

// Memoized selector for sorted variations by character
export const selectSortedVariationsByCharacter = (characterId) => createSelector(
  [selectVariationsByCharacter(characterId)],
  (variations) => {
    return [...variations].sort((a, b) => a.name.localeCompare(b.name));
  }
);

// Memoized selector for variations loading state
export const selectVariationsLoadingState = (characterId) => createSelector(
  [state => state.user.voiceActor.variations.loadingStates],
  (loadingStates) => loadingStates[characterId] || 'idle'
);

// Memoized selector for variation operation status
export const selectVariationOperationStatus = (operation) => createSelector(
  [state => state.user.voiceActor.variations.operationStatus],
  (operationStatus) => operationStatus[operation]
);

// Memoized selector for variation operation error
export const selectVariationOperationError = (operation) => createSelector(
  [state => state.user.voiceActor.variations.operationErrors],
  (operationErrors) => operationErrors[operation]
);

// Memoized selector for valid variations (filtering out stale references)
export const selectValidVariations = createSelector(
  [state => state.user.voiceActor.variations.byId],
  (byId) => {
    return Object.values(byId).filter(variation =>
      variation &&
      variation.uuid &&
      typeof variation.uuid === 'string'
    );
  }
);

// Selection selectors
export const selectSelectionId = (state, level) =>
  state.user.voiceActor.selectionId?.[level] || null;
export const selectPersistSelections = (state) => state.user.voiceActor.persistSelections;

// New selectors that derive selected entities from selection IDs
export const selectActorUserId = (state) =>
  state.user.voiceActor.selectionId?.actorUser || null;

export const selectPortfolioId = (state) =>
  state.user.voiceActor.selectionId?.portfolio || null;

export const selectCharacterId = (state) =>
  state.user.voiceActor.selectionId?.character || null;

export const selectVariationId = (state) =>
  state.user.voiceActor.selectionId?.variation || null;

// Create selectors that get the full entity based on the selection ID
export const selectSelectedActorUser = createSelector(
  [selectActorUserId, selectActorUsers],
  (actorUserId, actorUsers) => {
    if (!actorUserId || !actorUsers) return null;
    return actorUsers.find(actor => actor.uuid === actorUserId) || null;
  }
);

export const selectSelectedPortfolio = createSelector(
  [selectPortfolioId, selectActorPortfolios],
  (portfolioId, portfolios) => {
    if (!portfolioId || !portfolios) return null;
    return portfolios.find(portfolio => portfolio.uuid === portfolioId) || null;
  }
);

export const selectSelectedCharacter = createSelector(
  [selectCharacterId, selectRecordedCharacters],
  (characterId, characters) => {
    if (!characterId || !characters) return null;
    return characters.find(character => character.uuid === characterId) || null;
  }
);

export const selectSelectedVariation = createSelector(
  [selectVariationId, state => state.user.voiceActor.variations.byId],
  (variationId, variationsById) => {
    if (!variationId || !variationsById) return null;
    return variationsById[variationId] || null;
  }
);

// Cache selectors
export const selectCache = (state) => state.user.voiceActor.cache;

// Signed URL selectors
export const selectSignedUrlStatus = (state) => state.user.voiceActor.signedUrlStatus;
export const selectSignedUrl = (state, originalUrl, userId) => {
  if (!originalUrl || !userId) return null;
  const key = `${originalUrl}_${userId}`;
  return state.user.voiceActor.signedUrls[key] || null;
};

// Voice Lab specific selector that combines all needed data
export const selectVoiceLabData = createSelector(
  [
    selectActorUsers,
    selectActorPortfolios,
    selectEnhancedCharacters,
    selectCharacterVariationsMap,
    state => state.user.voiceActor.status,
    state => state.user.voiceActor.portfolioStatus,
    state => state.user.voiceActor.characterStatus,
    state => state.user.voiceActor.variationStatus
  ],
  (actorUsers, portfolios, characters, variationsMap, actorStatus, portfolioStatus, characterStatus, variationStatus) => ({
    actorUsers,
    portfolios,
    characters,
    variationsMap,
    isDataLoaded: actorUsers.length > 0, // Consider data loaded if we have actor users, even if other data is empty
    loadingStates: {
      actorUsers: actorStatus,
      portfolios: portfolioStatus,
      characters: characterStatus,
      variations: variationStatus
    }
  })
);

// Export reducer
export default voiceActorSlice.reducer;
